import { SelectOptionType, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'reactstrap';

import {
  financialPortalPaymentHistory,
  financialPortalPaymentHistoryDownload,
} from '../../../api/requests/financialPortal';
import { getIdentificationTypes } from '../../../api/requests/globalParameters';
import SectionTitle from '../../../components/text/SectionTitle';
import { downloadFromBlob } from '../../../utils/blob';
import { PaymentHistoryDetailModal } from '../shared/PaymentHistoryDetailModal';

import { PaymentHistoryTable } from './parts/PaymentHistoryTable';
import { SearchControls } from './parts/SearchControls';
import { PaymentHistoryFilters, PaymentHistoryRow } from './types';

const dictPrefix = 'financialPortal.paymentHistory';
const ITEMS_PER_PAGE = 10;

export default function FinancialPortalPaymentHistory() {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState<PaymentHistoryRow[]>([]);
  const [filters, setFilters] = useState<PaymentHistoryFilters>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [identificationTypes, setIdentificationTypes] = useState<
    SelectOptionType[]
  >([]);

  const paymentTypeOptions = useMemo<SelectOptionType[]>(
    () => [
      { value: 'CR', label: t(`${dictPrefix}.paymentTypes.credit`) },
      { value: 'CO', label: t(`${dictPrefix}.paymentTypes.cash`) },
    ],
    [t],
  );

  const handleSearch = useCallback(
    async ({
      searchParams,
      page,
    }: {
      searchParams: PaymentHistoryFilters;
      page: number;
    }) => {
      setIsLoading(true);
      const { data, error } = await financialPortalPaymentHistory({
        ...searchParams,
        items: ITEMS_PER_PAGE,
        page,
      });
      if (error) {
        addToast({
          text: t(`${dictPrefix}.errorOnSearch`),
          color: 'danger',
          icon: 'error',
        });
      } else {
        setTableData(data?.data ?? []);
        setTotalPages(data?.total_pages || 0);
        setTotalItems(data?.total || 0);
      }
      setIsLoading(false);
    },
    [t],
  );

  const handleDownload = useCallback(async () => {
    setIsDownloadLoading(true);
    const { data, error, status } = await financialPortalPaymentHistoryDownload(
      filters,
    );
    if (status === 204) {
      addToast({
        text: t(`${dictPrefix}.generatingFile.empty`),
        color: 'warning',
        icon: 'warning',
      });
      setIsDownloadLoading(false);
      return;
    }
    if (error) {
      addToast({
        text: t(`${dictPrefix}.generatingFile.errorOnDownload`),
        color: 'danger',
        icon: 'error',
      });
      setIsDownloadLoading(false);
      return;
    }
    if (status === 200 && data) {
      addToast({
        text: t(`${dictPrefix}.generatingFile.download`),
        color: 'success',
        icon: 'success',
      });
      const blob = new Blob([data], { type: 'vnd.ms-excel' });
      downloadFromBlob(blob, `historial_pagos.xls`);
    }
    setIsDownloadLoading(false);
  }, [filters, t]);

  const handleClear = useCallback(() => {
    setFilters({});
    setCurrentPage(0);
  }, []);

  const [detailRowId, setDetailRowId] = useState<string | null>(null);

  const handleClickDetail = useCallback((row: PaymentHistoryRow) => {
    setDetailRowId(row.id);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailRowId(null);
  }, []);

  const handleDownloadAttachment = useCallback((url: string) => {
    window.open(url, '_blank');
  }, []);

  useEffect(() => {
    handleSearch({ searchParams: filters, page: currentPage });
  }, [handleSearch, currentPage, filters]);

  useEffect(() => {
    let mounted = true;
    getIdentificationTypes(null).then(({ data }) => {
      if (!mounted) return;
      setIdentificationTypes(
        data?.identificationTypes?.map((it) => ({
          value: it.id,
          label: it.name,
        })) ?? [],
      );
    });
    return () => {
      mounted = false;
    };
  }, []);

  const hasFilters = useMemo(
    () =>
      Boolean(
        filters.name ||
          filters.identification ||
          filters.identificationTypeId ||
          filters.voucherNumber ||
          filters.paymentDate ||
          filters.paymentType,
      ),
    [filters],
  );

  return (
    <Card className="mt-5 mx-3 p-4">
      <SectionTitle text={t(`${dictPrefix}.title`)} />
      <p>{t(`${dictPrefix}.subtitle`)}</p>

      <SearchControls
        identificationTypes={identificationTypes}
        paymentTypeOptions={paymentTypeOptions}
        isDownloadLoading={isDownloadLoading}
        onSearch={(f) => {
          setCurrentPage(0);
          setFilters(f);
        }}
        onClear={handleClear}
        onDownload={handleDownload}
      />
      <PaymentHistoryTable
        data={tableData}
        isLoadingResults={isLoading}
        totalItems={totalItems}
        totalPages={totalPages}
        currentPage={currentPage}
        itemsPerPage={ITEMS_PER_PAGE}
        hasFilters={hasFilters}
        onChangePage={setCurrentPage}
        onClickDetail={handleClickDetail}
        onDownloadAttachment={handleDownloadAttachment}
      />
      <PaymentHistoryDetailModal
        paymentRowId={detailRowId}
        isOpen={detailRowId !== null}
        onClose={handleCloseDetail}
        includeStudent
      />
    </Card>
  );
}
